import AppLayout from '../layouts/AppLayout'
import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react'
import { AppContext } from '../contexts/AppContext'
import { Link, useNavigate, useParams } from 'react-router-dom'
import Spinner from 'react-activity/dist/Spinner'
import 'react-activity/dist/Spinner.css'
import { FiDownload } from 'react-icons/fi'
import WorkHeader from '../components/WorkHeader'
import { CONTENT_TYPES, contentTypeLabel, SEARCH_PLATFORMS } from '../constants/domain'
import '../styles/match.css'
import DataTable from '../components/DataTable'

const formatTimestamp = (timestamp) => {
  if (!timestamp) return ''
  let str = typeof timestamp === 'string' ? timestamp : String(timestamp)
  if (!str.endsWith('Z') && !str.includes('+') && !str.includes('-', 10)) str += 'Z'
  const date = new Date(str)
  if (isNaN(date.getTime())) return ''
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit' })
}

const formatElapsedTime = (addedat, deletedat) => {
  if (!addedat || !deletedat) return ''

  let addedStr = typeof addedat === 'string' ? addedat : String(addedat)
  let deletedStr = typeof deletedat === 'string' ? deletedat : String(deletedat)
  if (!addedStr.endsWith('Z') && !addedStr.includes('+') && !addedStr.includes('-', 10)) {
    addedStr += 'Z'
  }
  if (!deletedStr.endsWith('Z') && !deletedStr.includes('+') && !deletedStr.includes('-', 10)) {
    deletedStr += 'Z'
  }

  const addedMs = new Date(addedStr).getTime()
  const deletedMs = new Date(deletedStr).getTime()
  if (isNaN(addedMs) || isNaN(deletedMs)) return ''
  const diffMs = deletedMs - addedMs
  if (diffMs < 0) return ''
  const totalMinutes = Math.floor(diffMs / 60000)
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  if (hours === 0) return `${minutes} Minutes`
  if (minutes === 0) return `${hours} Hours`
  return `${hours} Hours ${minutes} Minutes`
}

const MemberMatchDetails = () => {
  const { matchId, competition } = useParams()

  const navigate = useNavigate()

  const [match, setMatch] = useState('')
  const [type, setType] = useState('Live')
  const [matchLinks, setmatchLinks] = useState([])
  const [activeSection, setActiveSection] = useState('links')
  const [linksLoading, setslinksLoading] = useState(false)
  const [liveCount, setliveCount] = useState(0)
  const [highlightCount, sethighlightCount] = useState(0)
  const [googleCount, setgoogleCount] = useState(0)
  const [totalSpectators, settotalSpectators] = useState(0)
  const [websitesLinks, setwebsitesLinks] = useState([])
  const [competitions, setCompetitions] = useState([])
  const [excelDownloading, setExcelDownloading] = useState(false)

  const { getmatch, getmatchlinks, downloadmatchlinksexcel, isLoading, getCompetitions } =
    useContext(AppContext)

  const fetchCompetitions = useCallback(async () => {
    const response = await getCompetitions()
    setCompetitions(response)
  }, [getCompetitions])

  const selectedCompetition = useMemo(
    () => competitions.find((comp) => comp.id === Number(competition)),
    [competitions, competition]
  )

  useEffect(() => {
    fetchCompetitions()
  }, [fetchCompetitions])

  useEffect(() => {
    if (matchId) {
      const fetchMatch = async () => {
        const response = await getmatch(matchId)
        if (response) {
          if (response[0] !== '') {
            setMatch(response[0])
          } else {
            navigate('/matches')
          }
        } else {
          navigate('/matches')
        }
      }

      fetchMatch()
    }
  }, [matchId, getmatch, navigate])

  const fetchMatchlinks = async (type) => {
    setslinksLoading(true)
    const response = await getmatchlinks(type, matchId)
    setmatchLinks(response.matchlinks)
    setliveCount(response.totallive)
    sethighlightCount(response.totalhighlights)
    setgoogleCount(response.totalgoogle)
    setwebsitesLinks(response.totalwebsites)
    settotalSpectators(response.totalspectators)
    setslinksLoading(false)
  }

  useEffect(() => {
    if (matchId) {
      fetchMatchlinks(type)
    }
  }, [type, matchId])

  const handleSetType = async (t) => {
    setType(t)
    setActiveSection('links')
    fetchMatchlinks(t)
  }

  const handleDownloadExcel = async () => {
    if (excelDownloading) return
    try {
      setExcelDownloading(true)
      const filenameHint = match && match.title ? match.title : matchId
      await downloadmatchlinksexcel(matchId, filenameHint)
    } finally {
      setExcelDownloading(false)
    }
  }

  const getDomain = (link, domain) => {
    if (domain) return domain
    try {
      return new URL(link).hostname
    } catch {
      return ''
    }
  }

  const websiteIconMap = useMemo(() => {
    const map = new Map()
    if (websitesLinks) {
      websitesLinks.forEach((website) => {
        if (website.link) {
          website.link.split(',').forEach((domain) => {
            map.set(domain.trim().toLowerCase(), website.icon)
          })
        }
      })
    }
    return map
  }, [websitesLinks])

  const getWebsiteIcon = (link) => {
    try {
      const hostname = new URL(link).hostname.toLowerCase().replace(/^www\./, '')
      for (const [domain, icon] of websiteIconMap) {
        if (hostname === domain || hostname.endsWith('.' + domain)) {
          return icon
        }
      }
    } catch {}
    return null
  }

  const linkTableColumns = useMemo(() => {
    const cols = []
    cols.push({
      Header: 'LINK',
      accessor: 'link',
      Cell: ({ value }) => {
        const icon = getWebsiteIcon(value)
        return (
          <div className='flex items-center gap-2'>
            {icon && (
              <img
                width='16'
                height='16'
                src={`${process.env.REACT_APP_STATIC_URL || ''}${icon}`}
              />
            )}
            <a
              href={value}
              target='_blank'
              rel='noreferrer'
              className='text-blue-600 hover:underline truncate block max-w-[400px]'
            >
              {value}
            </a>
          </div>
        )
      },
    })
    cols.push(
      { Header: 'TYPE', accessor: 'type', Cell: ({ value }) => contentTypeLabel(value) },
      {
        Header: 'DOMAIN',
        accessor: (row) => getDomain(row.link, row.domain),
        id: 'domain',
      },
      {
        Header: 'NOTICED TIME',
        accessor: 'addedat',
        Cell: ({ value }) => (value ? formatTimestamp(value) : '–'),
      },
      {
        Header: 'TAKE DOWN TIME',
        accessor: 'deletedat',
        Cell: ({ value }) => (value ? formatTimestamp(value) : '–'),
      },
      {
        Header: 'TOTAL TIME ELAPSED',
        id: 'elapsedtime',
        Cell: ({ row }) => {
          const { addedat, deletedat } = row.original
          const result = formatElapsedTime(addedat, deletedat)
          return result || '–'
        },
      }
    )
    return cols
  }, [type, websiteIconMap])

  return (
    <AppLayout pageName='Work Details'>
      {match !== '' ? (
        <>
          <WorkHeader work={match} />
          <section className='relative'>
            <button
              type='button'
              onClick={handleDownloadExcel}
              disabled={excelDownloading}
              className='btn-main-inverse absolute top-0 right-0 disabled:opacity-60 m-2'
            >
              <span>Download Excel File</span>
              {excelDownloading ? (
                <Spinner
                  size={14}
                  animating={excelDownloading}
                />
              ) : (
                <FiDownload />
              )}
            </button>
            <div className='flex flex-col lg:flex-col justify-between items-center space-x-0 lg:space-x-3'>
              <div className='text-center'>
                <h2 className='text-2xl font-bold tracking-normal text-main mb-2'>Infringing Links</h2>
                <div className='flex flex-row items-center space-x-3 mb-2'>
                  <div>
                    <span className='bg-blue-100 text-blue-800 text-sm font-medium px-3 py-2 rounded'>
                      Total links:{' '}
                      {liveCount + highlightCount + googleCount > 0
                        ? (liveCount + highlightCount + googleCount).toLocaleString()
                        : '0'}
                    </span>
                  </div>
                  <div>
                    <span className='bg-green-100 text-green-800 text-sm font-medium px-3 py-2 rounded'>
                      Listens / Views:
                      {totalSpectators > 0
                        ? ` ${Intl.NumberFormat('en-US').format(parseInt(totalSpectators)).replace(/,/g, ' ')}`
                        : 0}
                    </span>
                  </div>
                </div>
              </div>
              {websitesLinks && (
                <div className='flex flex-wrap flex-row items-center justify-center mb-4'>
                  {websitesLinks.map((website, index) => {
                    return (
                      <React.Fragment key={index}>
                        <div className='flex space-x-2 border rounded p-1 pr-2 m-2 items-center'>
                          <div>
                            <img
                              width='40'
                              height='40'
                              src={`${process.env.REACT_APP_STATIC_URL || ''}${website.icon}`}
                            />
                          </div>
                          <div>
                            <h6 className='text-neutral-500 text-xs'>{website.name}</h6>
                            <h5 className='text-main-alt leading-none mt-0.5 font-bold'>
                              {SEARCH_PLATFORMS.includes(website.name) ? googleCount : website.total}
                            </h5>
                          </div>
                        </div>
                      </React.Fragment>
                    )
                  })}
                </div>
              )}
            </div>
            <div className='pills w-full rounded-lg p-0 text-black font-semibold flex flex-row'>
              {CONTENT_TYPES.map((contentType) => {
                const count = { Live: liveCount, Highlight: highlightCount, Google: googleCount }[contentType.value]
                return (
                  <button
                    key={contentType.value}
                    className={`${
                      activeSection === 'links' && type === contentType.value
                        ? 'bg-white border border-b-white'
                        : 'hover:bg-neutral-50 border-b text-neutral-500'
                    } rounded-t-lg w-1/3 p-4 flex justify-center space-x-2`}
                    onClick={() => {
                      setActiveSection('links')
                      handleSetType(contentType.value)
                    }}
                  >
                    <span>{contentType.label}</span>
                    {count > 0 && (
                      <span className='bg-green-100 text-green-800 text-sm font-medium px-2.5 py-0.5 rounded'>{count}</span>
                    )}
                  </button>
                )
              })}
            </div>
            {activeSection === 'links' && (
              <section className='space-y-6 !rounded-t-none !border-t-0'>
                <div>
                  {linksLoading ? (
                    <div className='flex justify-center w-full py-12'>
                      <Spinner
                        color='#CCCCCC'
                        size={24}
                      />
                    </div>
                  ) : (
                    <DataTable
                      data={matchLinks}
                      columns={linkTableColumns}
                      defaultPageSize={50}
                      pageSizeOptions={[10, 25, 50, 100]}
                    />
                  )}
                </div>
              </section>
            )}
          </section>
        </>
      ) : (
        <div className='flex justify-center w-full py-12'>
          <Spinner
            color='#CCCCCC'
            size={24}
          />
        </div>
      )}
    </AppLayout>
  )
}

export default MemberMatchDetails
